// Simplified local-data adapter for CPGridV2.
// Handles client-side sort + paging only. Filtering is done externally
// (pass the already-filtered array via `data`) so filter state never lives here.

import * as React from "react";
import { DataResult, process } from "@progress/kendo-data-query";
import {
  GridColumnProps,
  GridDataStateChangeEvent,
  GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import {
  CPGridV2Adapter,
  CPGridV2Column,
  CPGridV2ColumnUpdate,
  CPGridV2State,
} from "../types";

// Internal helpers
/**
 * Merge updated Kendo column state (after resize/reorder) back into the
 * CPGridV2Column definitions so hide/pinned/renderCell props are preserved.
 */
const mergeColumnState = (
  columns: CPGridV2Column[],
  updatedColumns: GridColumnProps[],
): CPGridV2Column[] =>
  updatedColumns.map((updated) => {
    const existing = columns.find((c) => c.field === updated.field) ?? {};
    return { ...existing, ...updated } as CPGridV2Column;
  });

// Options
export interface UseCPGridV2Options {
  /** Initial column definitions. The adapter tracks show/hide/order/width mutations. */
  columns: CPGridV2Column[];

  /**
   * The data rows to display. This should already be filtered by the caller
   * (via `useState`, `react-query`, or a manual fetch) — the adapter does **not**
   * apply any filtering. When this array changes, the grid automatically resets
   * to page 1 to avoid landing on an out-of-range page.
   */
  data: any[];

  /**
   * Default number of rows per page.
   * @default 20
   */
  defaultPageSize?: number;

  /**
   * Pass an external loading flag here (e.g. the `isFetching` value from
   * react-query) so the grid shows its overlay spinner while data is loading.
   * @default false
   */
  showLoader?: boolean;

  /**
   * The server's true total record count (e.g. `GridFetchResponse.totalRecords`).
   *
   * Pass this whenever `data` is already the current server-fetched page
   * (the standard case: a search endpoint driven by `{ take, skip, sortBy,
   * ascending, ...filters }`). When provided, the adapter treats `data` as
   * already sorted/paged by the server and passes it through as-is instead
   * of re-slicing it — otherwise a second page of `take` rows would get
   * `skip`/`take` applied to it *again* and render empty.
   *
   * Omit only for a fully local, unpaginated dataset — the adapter then
   * falls back to sorting/paging `data` client-side, as before.
   */
  totalRecords?: number;
}

// Hook
/**
 * Adapter hook for `CPGridV2`.
 *
 * Handles **client-side sort + paging** over a pre-filtered data array.
 * Because filters live outside the grid (in your component's state or a query),
 * every `data` change triggers an automatic page-1 reset.
 *
 * @example
 * ```tsx
 * // 1. Call the hook with your (filtered) data
 * const adapter = useCPGridV2({
 *   columns: myColumns,
 *   data: filteredRows,        // already filtered; changes re-render the grid
 *   showLoader: isFetching,
 * });
 *
 * // 2. Pass the adapter to CPGridV2
 * <CPGridV2
 *   name="users-grid"
 *   title="Users"
 *   adapter={adapter}
 *   pageable
 * />
 * ```
 */
const useCPGridV2 = (options: UseCPGridV2Options): CPGridV2Adapter => {
  const pageSize = options.defaultPageSize ?? 20;

  const [gridState, setGridState] = React.useState<CPGridV2State>({
    columns: options.columns,
    controls: { take: pageSize, skip: 0, sort: undefined },
  });

  const isServerPaged = options.totalRecords != null;

  // Auto-reset page when the data array size changes. Only meaningful in
  // client-side mode — in server mode `data` is just the current page, so
  // its length changing (e.g. a shorter last page) does not mean we should
  // jump back to page 1.
  const prevDataLength = React.useRef<number>(options.data.length);

  React.useEffect(() => {
    if (isServerPaged) return;
    if (options.data.length !== prevDataLength.current) {
      prevDataLength.current = options.data.length;
      setGridState((prev) => ({
        ...prev,
        controls: { ...prev.controls, skip: 0 },
      }));
    }
  }, [options.data.length, isServerPaged]);

  // Process data (sort + page)
  const records = React.useMemo<DataResult>(() => {
    // Server-paged mode: `data` is already the current page, sorted and
    // filtered by the server — pass it through untouched.
    if (isServerPaged) {
      return { data: options.data, total: options.totalRecords ?? 0 };
    }

    try {
      return process(options.data, {
        sort: gridState.controls.sort,
        skip: gridState.controls.skip,
        take: gridState.controls.take,
      });
    } catch {
      // Graceful fallback — never crash the grid due to process errors
      return { data: options.data, total: options.data.length };
    }
  }, [options.data, options.totalRecords, isServerPaged, gridState.controls]);

  // Event handlers
  const onSort = (event: GridSortChangeEvent) => {
    setGridState((prev) => ({
      ...prev,
      controls: { ...prev.controls, sort: event.sort, skip: 0 },
    }));
  };

  /** Handles paging via `onDataStateChange` on the Kendo Grid. */
  const onGridStateUpdate = (event: GridDataStateChangeEvent) => {
    setGridState((prev) => ({
      ...prev,
      controls: { ...event.dataState },
    }));
  };

  /** Merge Kendo column reorder/resize events and show/hide changes back into state. */
  const onColumnUpdate = (state: CPGridV2ColumnUpdate) => {
    const base = state.columns ?? gridState.columns;
    const updated = mergeColumnState(base, state.updatedColumns ?? base);
    setGridState((prev) => ({ ...prev, columns: updated }));
  };

  /** Reset the grid to the first page. Useful when external filters are applied. */
  const resetPaging = () => {
    setGridState((prev) => ({
      ...prev,
      controls: { ...prev.controls, skip: 0 },
    }));
  };

  /**
   * Returns the full sorted (but un-paged) dataset for Excel export.
   * This ensures the exported file contains all rows, not just the current page.
   */
  const exportData = (): any[] =>
    process(options.data, { sort: gridState.controls.sort }).data;

  return {
    records,
    gridState,
    onGridStateUpdate,
    onSort,
    onColumnUpdate,
    getColumnState: () => gridState.columns,
    showLoader: options.showLoader ?? false,
    exportData,
    resetPaging,
  };
};

export default useCPGridV2;
